/**
 * TikZ figures, step 3: authoring (design-handoffs/2026-10-07-tikz-figures,
 * 1a split view · 1b templates · 1d colour names · 1e keyboard and narrow).
 *
 * The pure part of the split view: the draft route, the redraw timing, what
 * the status chip says, which layout a width gets, the splitter's memory, the
 * theme colour names, and the preamble lines a template needs.
 *
 *   POST /api/documents/{doc}/blocks/{block}/figure/draft  { source }
 *        → 200 image/svg+xml (X-Draw-Ms)  |  422 FigureError
 *   GET  /api/documents/{doc}/blocks/{block}/figure/colours
 *        → { "lilia-ink": "#…", … } resolved for this figure
 */

function blockBase(documentId: string, blockId: string): string {
  return `/api/documents/${encodeURIComponent(documentId)}/blocks/${encodeURIComponent(blockId)}`;
}

export function figureDraftUrl(documentId: string, blockId: string): string {
  return `${blockBase(documentId, blockId)}/figure/draft`;
}

export function figureColoursUrl(documentId: string, blockId: string): string {
  return `${blockBase(documentId, blockId)}/figure/colours`;
}

// Redraw timing -------------------------------------------------------------------

/** Redraw this long after typing stops. */
export const REDRAW_DEBOUNCE_MS = 600;
/** …or this long, once drawing is measured to be slow. */
export const SLOW_REDRAW_DEBOUNCE_MS = 1000;
/** "Slow": the median measured draw is over this. */
export const SLOW_DRAW_MEDIAN_MS = 2000;
/** While drawing, the status adds the elapsed time once past this. */
export const SHOW_ELAPSED_AFTER_MS = 2000;
/** How many recent draws the median is taken over. */
export const DRAW_SAMPLES = 5;

export function median(xs: readonly number[]): number | null {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  const mid = s.length >> 1;
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

/** The debounce for the draws measured so far (the handoff's question 1). */
export function redrawDelay(samples: readonly number[]): number {
  const m = median(samples);
  return m !== null && m > SLOW_DRAW_MEDIAN_MS ? SLOW_REDRAW_DEBOUNCE_MS : REDRAW_DEBOUNCE_MS;
}

/** 0.3 s · 1.2 s · 12 s: one decimal under ten seconds, whole seconds after. */
export function formatSeconds(ms: number): string {
  const s = Math.max(0, ms) / 1000;
  return s < 10 ? `${(Math.round(s * 10) / 10).toFixed(1)} s` : `${Math.round(s)} s`;
}

// The status chip -----------------------------------------------------------------

export type DraftState = "empty" | "drawing" | "drawn" | "error";

export interface DraftStatus {
  state: DraftState;
  /** drawn: how long the draw took. */
  drawMs?: number | null;
  /** drawing: how long it has been going. */
  elapsedMs?: number;
  /** error: the failing line, when TeX named one. */
  line?: number | null;
  /** error: said instead (a source over the route's limit). */
  message?: string | null;
}

/**
 * How long a drawing took, for "Drawn · 0.7 s" and "drew in 0.7 s". Null for
 * a drawing that came from the cache (it took no visible time, and "0.0 s"
 * reads as a fault), so the label is just "Drawn".
 */
export function drawTime(ms: number | null | undefined): string | null {
  return ms != null && ms >= 50 ? formatSeconds(ms) : null;
}

export function statusText(s: DraftStatus): string {
  switch (s.state) {
    case "empty":
      return "Nothing to draw yet";
    case "drawing":
      return (s.elapsedMs ?? 0) > SHOW_ELAPSED_AFTER_MS ? `Drawing… ${formatSeconds(s.elapsedMs!)}` : "Drawing…";
    case "drawn":
      return drawTime(s.drawMs) ? `Drawn · ${drawTime(s.drawMs)}` : "Drawn";
    case "error":
      if (s.message) return s.message;
      return s.line != null ? `Line ${s.line} · didn’t draw` : "Didn’t draw";
  }
}

// Layout by width (1e) -------------------------------------------------------------

export type SplitLayout = "side" | "stacked" | "tabs";

/** Workspace ≥ 1100 px: side by side; 760–1099: stacked; < 760: two tabs. */
export function layoutForWidth(width: number): SplitLayout {
  if (width >= 1100) return "side";
  if (width >= 760) return "stacked";
  return "tabs";
}

// The splitter, remembered per person ------------------------------------------------

export const SPLIT_STORAGE_KEY = "lilia-tikz-split";
/** The source's share of the width: the drawing's 1.3 : 1. */
export const DEFAULT_SPLIT = 1.3 / 2.3;
export const MIN_SPLIT = 0.25;
export const MAX_SPLIT = 0.75;

export function clampSplit(f: number): number {
  if (!Number.isFinite(f)) return DEFAULT_SPLIT;
  return Math.min(MAX_SPLIT, Math.max(MIN_SPLIT, f));
}

export function readSplit(): number {
  try {
    const raw = window.localStorage.getItem(SPLIT_STORAGE_KEY);
    if (raw == null) return DEFAULT_SPLIT;
    return clampSplit(Number(raw));
  } catch {
    return DEFAULT_SPLIT;
  }
}

export function writeSplit(f: number): void {
  try {
    window.localStorage.setItem(SPLIT_STORAGE_KEY, String(Math.round(clampSplit(f) * 1000) / 1000));
  } catch {
    /* private mode: the split is just not remembered */
  }
}

// Theme colours as TikZ colour names (1d) ---------------------------------------------

export interface ColourName {
  name: string;
  /** What it is, in the autocomplete's right column. */
  note: string;
}

export const LILIA_COLOUR_NAMES: readonly ColourName[] = [
  { name: "lilia-accent", note: "the theme's accent" },
  { name: "lilia-accent-soft", note: "accent at 18 % on paper" },
  { name: "lilia-chapter", note: "this figure's chapter" },
  { name: "lilia-ink", note: "text" },
  { name: "lilia-paper", note: "paper" },
  ...[1, 2, 3, 4, 5, 6, 7, 8].map((i) => ({ name: `lilia-seq${i}`, note: `series ${i}` })),
];

export const COLOUR_FOOTNOTE = "Follows the theme. Literal colours never change.";

/** A resolved palette: name → #hex, for this figure. */
export type FigureColours = Record<string, string>;

/** Only string values that look like colours survive. */
export function parseFigureColours(body: unknown): FigureColours {
  const out: FigureColours = {};
  if (!body || typeof body !== "object") return out;
  for (const [k, v] of Object.entries(body as Record<string, unknown>)) {
    if (k.startsWith("lilia-") && typeof v === "string" && /^#[0-9a-f]{3,8}$/i.test(v.trim())) out[k] = v.trim();
  }
  return out;
}

/** The lilia- word being typed just before the caret, or null. */
export function colourPrefixBefore(textBefore: string): string | null {
  const m = /(?:^|[^\w-])(lilia-[\w-]*)$/.exec(textBefore);
  return m ? m[1] : null;
}

/** The names that complete a prefix, in the list's order; nothing once it is complete. */
export function matchColourNames(prefix: string): ColourName[] {
  const hits = LILIA_COLOUR_NAMES.filter((c) => c.name.startsWith(prefix));
  return hits.length === 1 && hits[0].name === prefix ? [] : hits;
}

/**
 * What the autocomplete says beside a name: the resolved note, with
 * "= accent here" when the chapter colour is the accent in this theme.
 */
export function colourNote(c: ColourName, colours: FigureColours): string {
  if (c.name === "lilia-chapter" && colours["lilia-chapter"] && colours["lilia-chapter"] === colours["lilia-accent"]) {
    return "= accent here";
  }
  return c.note;
}

// The preamble lines a template needs (1b) ---------------------------------------------

export type PreambleNeed = { kind: "package"; name: string } | { kind: "library"; name: string };

/** The lines in a preamble, comments taken off. */
function liveLines(preamble: string): string[] {
  return preamble.split("\n").map((l) => l.replace(/(^|[^\\])%.*$/, "$1"));
}

function listed(preamble: string, re: RegExp): Set<string> {
  const names = new Set<string>();
  for (const line of liveLines(preamble)) {
    for (const m of line.matchAll(re)) {
      for (const n of m[1].split(",")) if (n.trim()) names.add(n.trim());
    }
  }
  return names;
}

export function preambleHas(preamble: string, need: PreambleNeed): boolean {
  const re = need.kind === "package" ? /\\usepackage\s*(?:\[[^\]]*\])?\s*\{([^}]*)\}/g : /\\usetikzlibrary\s*\{([^}]*)\}/g;
  return listed(preamble, re).has(need.name);
}

/**
 * The lines to add for what's missing: one \usepackage per package, and the
 * libraries together on one \usetikzlibrary line. Nothing when all are there.
 */
export function missingPreambleLines(preamble: string, needs: readonly PreambleNeed[]): string[] {
  const missing = needs.filter((n) => !preambleHas(preamble, n));
  const lines = missing.filter((n) => n.kind === "package").map((n) => `\\usepackage{${n.name}}`);
  const libs = missing.filter((n) => n.kind === "library").map((n) => n.name);
  if (libs.length) lines.push(`\\usetikzlibrary{${libs.join(",")}}`);
  return lines;
}

export function addPreambleLines(preamble: string, lines: readonly string[]): string {
  if (!lines.length) return preamble;
  const base = preamble.replace(/\s+$/, "");
  return (base ? `${base}\n` : "") + lines.join("\n");
}

/** Takes the added lines back out (each once), leaving anything typed since. */
export function removePreambleLines(preamble: string, lines: readonly string[]): string {
  const rows = preamble.split("\n");
  for (const line of lines) {
    const i = rows.lastIndexOf(line);
    if (i >= 0) rows.splice(i, 1);
  }
  return rows.join("\n").replace(/\s+$/, "");
}

/**
 * Preamble lines as they read best in one sentence: the TikZ libraries
 * gathered into one \usetikzlibrary{…} (as an author would write them),
 * other lines as they are. Only how they're shown; each line is still added
 * on its own, so Undo and later checks see the same lines.
 */
export function groupPreambleLines(lines: readonly string[]): string[] {
  const libs: string[] = [];
  const out: string[] = [];
  let at = -1;
  for (const line of lines) {
    const m = /^\\usetikzlibrary\{([^}]*)\}$/.exec(line.trim());
    if (!m) {
      out.push(line);
      continue;
    }
    // The gathered line sits where the first library was.
    if (at < 0) at = out.push("") - 1;
    for (const lib of m[1].split(",").map((l) => l.trim()).filter(Boolean)) if (!libs.includes(lib)) libs.push(lib);
  }
  if (at >= 0) out[at] = `\\usetikzlibrary{${libs.join(",")}}`;
  return out;
}

/** "Added \usetikzlibrary{positioning} to the preamble." */
export function preambleNotice(lines: readonly string[]): string {
  lines = groupPreambleLines(lines);
  if (!lines.length) return "";
  const list = lines.length === 1 ? lines[0] : `${lines.slice(0, -1).join(", ")} and ${lines[lines.length - 1]}`;
  return `Added ${list} to the preamble.`;
}

/** What "Describe it instead" puts in Ask Lilia's input. */
export function describePrompt(text: string): string {
  return `Draw this as a TikZ figure: ${text.trim()}`;
}
